import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

import {ResourceType} from 'common/report/constants';
import {insertAtIndex, replaceByIndex} from 'common/util/array';
import {DisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {FILTER_VALUE_ATTRIBUTES, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  getFirstSegmentValue,
  getSegmentValueResourceType,
  isCohortSegment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {Segmentation} from 'common/types/reports/query';

import {
  isFilterEntry,
  isMetricEntry,
  isPropertyEntry,
  isSegmentEntry,
  MetricEntry,
  QueryFilterEntry,
  QuerySegmentEntry,
  QuerySuperEntry,
  SelectorTypeDefinitions,
} from '../types';
import {FilterEntryRow} from '../row-types/filter';
import {createEntryRow} from '../row-types/util';

function filterToBreakdown(entry: QueryFilterEntry) {
  //Filter to breakdown
  if (entry.event.resourceType === ResourceType.Cohort || isFirstTimeEventFilter(entry.event as PropertyFilter)) {
    // skip cohorts and first time filters for now
    return undefined;
  } else {
    const event = omit(entry.event, FILTER_VALUE_ATTRIBUTES) as Segmentation;
    if (entry.event.listItemFilters?.length) {
      // for a list of objects breakdown, we set the selected sub-property (listItemGroup) based on the
      // first sub-property selected in the filter (listItemFilters)
      event.listItemGroup = omit(entry.event.listItemFilters[0], FILTER_VALUE_ATTRIBUTES);
      delete event.listItemFilters;
    }
    return {
      ...entry,
      event,
      type: SelectorTypeDefinitions.Segment,
    };
  }
}

export function segmentToFilter(entry: QuerySegmentEntry): QueryFilterEntry {
  if (isCohortSegment(entry.event)) {
    return undefined;
  }
  const firstEvent = (Array.isArray(entry.event) ? entry.event[0] : entry.event) as Partial<PropertyFilter>;
  const event = PropertyFilterFactory.createFromSegment(firstEvent, {displayContext: DisplayContext.Default});
  return {
    ...FilterEntryRow.getNewEntry(),
    event,
  };
}

function propertyToInlineFilter(section: QueryEntrySection, copiedEntry: QuerySuperEntry, index: number) {
  const row = section.getRows(true)[index];
  const filteringCapabilities = row && section.getRowFilteringCapabilities(row);
  const currentEntry = section.helpers.getEntries()[index];

  if (row && filteringCapabilities.canApplyFilter && isPropertyEntry(copiedEntry)) {
    const filter: PropertyFilter = (isSegmentEntry(copiedEntry) ? segmentToFilter(copiedEntry) : copiedEntry)
      .event as PropertyFilter;
    let entry = currentEntry;

    const isInlineFilter = isFirstTimeEventFilter(filter);
    if (isInlineFilter && !filteringCapabilities.canApplyFirstTimeEventFilter) {
      return undefined;
    }

    if (isMetricEntry(currentEntry)) {
      entry = {
        ...currentEntry,
        behavior: {
          ...currentEntry.behavior,
          filters: [...currentEntry.behavior.filters, filter],
        },
      };
    } else {
      entry = {
        ...currentEntry,
        filters: [...currentEntry.filters, filter],
      };
    }
    return entry;
  }
  return undefined;
}

function likeToLike(section: QueryEntrySection, entry: QuerySuperEntry, index: number) {
  const row = createEntryRow(entry.type, {index, entry, section}, {});
  const filteringCapabilities = row && section.getRowFilteringCapabilities(row);
  // special case for inline filter to global filter
  if (
    isFilterEntry(entry) &&
    !filteringCapabilities.canApplyFirstTimeEventFilter &&
    isFirstTimeEventFilter(entry.event as PropertyFilter)
  ) {
    return undefined;
  }

  return entry;
}

function isInvalidOtherMove(section: QueryEntrySection, entry: QuerySuperEntry) {
  const otherOptions = new Set([...section.attr(`other-filter-options`), ...section.attr(`other-group-by-options`)]);
  const isInvalidFilter =
    isFilterEntry(entry) &&
    entry.event.resourceType === ResourceType.Other &&
    !otherOptions.has(entry.event.propertyName as string);
  const isInvalidBreakdown =
    isSegmentEntry(entry) &&
    getSegmentValueResourceType(entry.event) === ResourceType.Other &&
    !otherOptions.has(getFirstSegmentValue(entry.event).propertyName as string);

  return isInvalidFilter || isInvalidBreakdown;
}

/** This function generates new entries given a section and a section we are trying to paste in */
export function addEntryFromExternalSection(
  section: QueryEntrySection,
  copiedEntry: QuerySuperEntry | MetricEntry,
  index: number,
  nextEntryKey: number,
): {
  entries: Array<QuerySuperEntry | MetricEntry>;
  entry: QuerySuperEntry | MetricEntry;
  index: number;
  modifyExisting: boolean;
} {
  // TODO: Add logic for metric entry once we have dragging control ready in metric entry
  if (isMetricEntry(copiedEntry)) {
    return undefined;
  }
  let entries = cloneDeep(section.helpers.getEntries());

  let entry;
  let modifyExisting = false;

  if (copiedEntry === undefined) {
    return undefined;
  }
  if (isInvalidOtherMove(section, copiedEntry)) {
    return undefined;
  } else if (section.helpers.canUseRowType(copiedEntry.type)) {
    entry = likeToLike(section, copiedEntry, index);
  } else if (isFilterEntry(copiedEntry) && section.helpers.canUseRowType(SelectorTypeDefinitions.Segment)) {
    entry = filterToBreakdown(copiedEntry);
  } else if (isSegmentEntry(copiedEntry) && section.helpers.canUseRowType(SelectorTypeDefinitions.Filter)) {
    entry = segmentToFilter(copiedEntry);
  } else if (propertyToInlineFilter(section, copiedEntry, index)) {
    entry = propertyToInlineFilter(section, copiedEntry, index);
    modifyExisting = true;
  } else {
    return undefined;
  }

  if (entry === undefined) {
    return undefined;
  }

  if (modifyExisting) {
    entries = replaceByIndex(entries, index, entry);
  } else {
    entries = insertAtIndex(entries, index, {...entry, entryKey: nextEntryKey});
  }
  return {entries, entry, index, modifyExisting};
}
